import {
  loadHeadlessCheckout,
  PurseHeadlessCheckout,
  PurseHeadlessCheckoutPrimaryMethod,
  PurseHeadlessCheckoutPrimaryToken,
  PurseHeadlessCheckoutUIOptions,
} from '@purse-eu/web-sdk';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from 'react-query';
import { useTheme } from 'styled-components';

import { logError } from '@savgroup-front-common/configuration/appInsights/AppInsights';
import {
  CURRENCIES,
  currentAppEnvironment,
} from '@savgroup-front-common/constants';
import { toast } from '@savgroup-front-common/core/atoms/Toast';
import { safeFormattedIntlString } from '@savgroup-front-common/core/formatters';
import { globalMessages } from '@savgroup-front-common/core/helpers';
import { PaymentProvider } from '@savgroup-front-common/types/Payment/PaymentProvider';
import { Kind } from '@/api/Payment/getPaymentSessionQuery';

import { useConfirmPaymentIntent } from '../hooks/useConfirmPaymentIntent';
import { useGetPaymentAccount } from '../hooks/useGetPaymentAccount';

import { getPursePublicEnvironment } from './helpers/getPurseEnvironment';
import useCreatePaymentIntent from './hooks/useCreatePaymentIntent';
import { useGetPaymentSession } from './hooks/useGetPaymentSession';
import messages from './messages';

interface UsePaymentStripeArgs {
  invoice: { id: string; priceWithTax: number; currencyTrigram: CURRENCIES };
  claimGroupId: string;
  onSubmitSuccess: () => Promise<void>;
  kindId: string;
  kind: Kind;
}

export const usePaymentPurse = ({
  invoice,
  claimGroupId,
  onSubmitSuccess,
  kindId,
  kind,
}: UsePaymentStripeArgs) => {
  const theme = useTheme();
  const { paymentAccountDto } = useGetPaymentAccount({ claimGroupId });

  if (
    !paymentAccountDto?.paymentProvider ||
    ![PaymentProvider.PURSE, PaymentProvider.CUSTOM].includes(
      paymentAccountDto?.paymentProvider,
    )
  ) {
    throw new Error('Payment provider is not Purse');
  }

  const {
    paymentIntentClientSecret,
    isLoading: isCreatePaymentIntentLoading,
    refetch: handleCreatePaymentIntent,
  } = useCreatePaymentIntent({
    invoiceId: invoice.id,
    onInvoiceAlreadyPaid: onSubmitSuccess,
  });

  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<PurseHeadlessCheckoutPrimaryMethod>();
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<
    PurseHeadlessCheckoutPrimaryMethod[]
  >([]);
  const [selectedPrimaryToken, setSelectedPrimaryToken] =
    useState<PurseHeadlessCheckoutPrimaryToken | null>(null);

  const [checkout, setCheckout] = useState<PurseHeadlessCheckout>();

  const [isPayButtonDisabled, setIsPayButtonDisabled] = useState(true);

  const { handleConfirmPaymentIntent } = useConfirmPaymentIntent({
    invoiceId: invoice.id,
    onSuccess: onSubmitSuccess,
  });

  const { isLoading: isGetPaymentSessionLoading } = useGetPaymentSession({
    kindId,
    kind,
    onConfirmPaymentIntent: handleConfirmPaymentIntent,
    onCreatePaymentIntent: handleCreatePaymentIntent,
  });

  const formRef = useRef<HTMLDivElement | null>(null);

  const paymentConfig: PurseHeadlessCheckoutUIOptions = useMemo(
    () => ({
      hostedForm: {
        panInputLabel: safeFormattedIntlString(messages.cardNumber),
        cvvInputLabel: safeFormattedIntlString(messages.cardCvv),
        expirationInputLabel: safeFormattedIntlString(messages.expirationDate),
        holderInputLabel: safeFormattedIntlString(messages.creditCardHolder),

        panCannotBeEmptyError: safeFormattedIntlString(
          globalMessages.form.required,
        ),
        panRequiredError: safeFormattedIntlString(globalMessages.form.required),
        panFormatError: safeFormattedIntlString(messages.panFormatError),

        cvvCannotBeEmptyError: safeFormattedIntlString(
          globalMessages.form.required,
        ),
        cvvRequiredError: safeFormattedIntlString(globalMessages.form.required),
        cvvFormatError: safeFormattedIntlString(messages.cvvFormatError),

        expirationRequiredError: safeFormattedIntlString(
          globalMessages.form.required,
        ),
        expirationCannotBeEmptyError: safeFormattedIntlString(
          globalMessages.form.required,
        ),
        expirationOutOfRangeError: safeFormattedIntlString(
          messages.expirationOutOfRangeError,
        ),

        holderFormatError: safeFormattedIntlString(
          globalMessages.form.required,
        ),
        holderRequiredError: safeFormattedIntlString(
          globalMessages.form.required,
        ),
        holderCannotBeEmptyError: safeFormattedIntlString(
          globalMessages.form.required,
        ),
        expirationFormatError: safeFormattedIntlString(
          messages.expirationFormatError,
        ),

        cvvPlaceholder: safeFormattedIntlString(messages.cvvPlaceholder),
        cvv4Placeholder: safeFormattedIntlString(messages.cvv4Placeholder),
        expirationPlaceholder: safeFormattedIntlString(
          messages.expirationPlaceholder,
        ),
        panPlaceholder: safeFormattedIntlString(messages.panPlaceholder),
      },
      theme: {
        global: {
          '::placeholder': { color: theme.colors.default },
          '::placeholder:disabled': { color: theme.colors.default },
          ':invalid': { color: theme.colors.danger },
          ':valid': { color: theme.colors.success },
          color: theme.newUI.defaultColors.secondaryText,
          fontFamily:
            'Inter,Roboto,Helvetica Neue, Arial, Helvetica, sans-serif',
          fontSize: theme.fonts.size.medium,
          fontSrc: 'Inter-latin-400.woff2',
          gap: '8px',
          margin: '0',
          padding: '0',
        },
        input: {
          ':invalid': {
            color: theme.colors.mainTextColor,
            borderColor: theme.colors.danger,
          },
          ':valid': {
            color: theme.colors.mainTextColor,
            borderColor: theme.colors.success,
          },
          ':focus': {
            borderWidth: '1px',
          },
          borderWidth: '1px',
          fontSize: '16px',
          fontWeight: theme.fonts.weight.normal.toString(),
          borderColor: theme.colors.default,
          borderRadius: theme.borders.radius,
          padding: '11.5px 17.5px',
          color: theme.colors.mainTextColor,
          fontStyle: 'italic',
        },
        helperText: {},
        label: {
          fontFamily:
            'Inter,Roboto,Helvetica Neue, Arial, Helvetica, sans-serif',
          fontSize: theme.fonts.size.medium,
          fontWeight: 'bold',
        },
        tooltip: {},
      },
    }),
    [
      theme.borders.radius,
      theme.colors.danger,
      theme.colors.default,
      theme.colors.mainTextColor,
      theme.colors.success,
      theme.fonts.size.medium,
      theme.fonts.weight.normal,
      theme.newUI.defaultColors.secondaryText,
    ],
  );

  const selectPaymentMethod = useCallback(
    (paymentMethod: PurseHeadlessCheckoutPrimaryMethod) => {
      const token =
        paymentMethod as unknown as PurseHeadlessCheckoutPrimaryToken;

      setSelectedPrimaryToken(token);

      const form = formRef.current;

      if (!form) {
        return;
      }

      form.innerHTML = '';

      const paymentElement = token.getPaymentElement(paymentConfig);

      paymentElement?.on('fatalError', (error) => {
        logError(error);

        toast.error(safeFormattedIntlString(messages.paymentFailure));
      });

      setSelectedPaymentMethod(paymentMethod);

      paymentElement.appendTo(form);
    },
    [paymentConfig],
  );

  useEffect(() => {
    if (!paymentIntentClientSecret) {
      return;
    }

    setIsCheckoutLoading(true);

    let unsubscribe: (() => void) | undefined;
    let disposed = false;

    loadHeadlessCheckout(getPursePublicEnvironment(currentAppEnvironment))
      .then(async (module) => {
        if (disposed) {
          return;
        }

        const checkout = await module.createHeadlessCheckout(
          paymentIntentClientSecret,
        );

        setCheckout(checkout);

        checkout.isPaymentFulfilled.subscribe((isReady) => {
          setIsPayButtonDisabled(!isReady);
        });

        const displayPaymentMethods = (
          methods: PurseHeadlessCheckoutPrimaryMethod[],
        ) => {
          setPaymentMethods(methods);

          setIsCheckoutLoading(false);
          const firstMethod = methods[0];

          if (methods.length === 1 && firstMethod) {
            const paymentElement = firstMethod?.getPaymentElement();

            paymentElement?.on('fatalError', (error) => {
              logError(error);

              toast.error(safeFormattedIntlString(messages.paymentFailure));
            });

            selectPaymentMethod(firstMethod);
          }
        };

        unsubscribe = checkout.paymentMethods.subscribe(
          displayPaymentMethods as any,
        );
      })
      .catch((e) => {
        setIsCheckoutLoading(false);

        logError(e);
      });

    return () => {
      disposed = true;
      try {
        unsubscribe?.();
      } catch {
        return;
      }
    };
  }, [paymentIntentClientSecret, selectPaymentMethod]);

  const { mutateAsync: handleSubmit, isLoading: isSubmitting } = useMutation(
    ['submitPayment'],
    async (e: any) => {
      e.preventDefault();
      e.stopPropagation();

      if (!checkout) {
        return;
      }

      try {
        await checkout.submitPayment();

        toast.success(safeFormattedIntlString(messages.paymentSuccess));
      } catch (error: any) {
        toast.error(safeFormattedIntlString(messages.paymentFailure));
      }
    },
  );

  return {
    paymentMethods,
    selectedPrimaryToken,
    selectPaymentMethod,
    formRef,
    isLoading: Boolean(isCreatePaymentIntentLoading || isCheckoutLoading),
    selectedPaymentMethod,
    handleSubmit,
    isPayButtonDisabled,
    isGetPaymentSessionLoading,
    isSubmitting,
  };
};
